/* global app */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { getBetgeniusStreamingUrl } from 'integrationActions.js';
import { translation } from 'helpers/utilsHelper.js';
import VideoIcon from 'video_icon.svg';
import S from './StyledGeniusLivePlayer.js';

// ========== UTILITY FUNCTIONS ==========

/**
 * Check if event has Genius Live Stream available
 * @param {Object} eventOrExtras - Event object or extras object
 * @returns {boolean}
 */
export const hasGeniusLiveStream = (eventOrExtras) => {
  try {
    const extras = eventOrExtras?.extras || eventOrExtras;
    if (!extras) return false;
    
    if (eventOrExtras?.providerId && eventOrExtras.providerId !== 2) return false;
    
    const phase = String(extras.BetgeniusStreamingPhase || '').toUpperCase();
    const rawData = extras.BetgeniusStreamingData;
    const hasData = !!rawData && rawData !== '[]' && rawData !== 'null';
    return phase === 'IN_PLAY' && hasData;
  } catch (e) {
    return false;
  }
};

const parseBetgeniusData = (raw) => {
  if (!raw) return null;
  let data = raw;
  try {
    while (typeof data === 'string') {
      const next = JSON.parse(data);
      if (next === data) break;
      data = next;
    }
    const result = Array.isArray(data) ? data[0] : data;
    if (result && typeof result === 'object' && result.id) {
      return result;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const pickPermission = (streamingData, deviceType) => {
  if (!streamingData?.permissions) return null;
  return streamingData.permissions.find((p) => p.device === deviceType) || streamingData.permissions[0];
};

const pickDelivery = (streamingData) => {
  if (!streamingData?.deliveries) return null;
  const entries = Object.entries(streamingData.deliveries).filter(
    ([, ids]) => Array.isArray(ids) && ids.length > 0
  );
  if (!entries.length) return null;
  const [deliveryType, variants] = entries[0];
  return { deliveryType, deliveryTypeId: variants[0].id };
};

const reloadWidgetScript = (src) => {
  const existingScripts = document.querySelectorAll('script[src*="widgetLoader"]');
  existingScripts.forEach((s) => s.remove());

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(s);
  });
};

const closePlayerIfExists = () => {
  try {
    if (window.GeniusLivePlayer?.player?.close) {
      window.GeniusLivePlayer.player.close();
    }
    window.GeniusLivePlayer = null;
  } catch (e) {
    /* ignore */
  }
};

/**
 * Generate or retrieve device-specific identifier
 * Stores in localStorage for persistence across sessions
 * @returns {string} Unique device ID
 */
const getOrCreateDeviceId = () => {
  const STORAGE_KEY = 'betgenius_deviceId';
  
  try {
    // Try to get existing deviceId from localStorage
    let deviceId = localStorage.getItem(STORAGE_KEY);
    
    if (deviceId && deviceId.length > 0) {
      return deviceId;
    }
    
    // Generate new deviceId if doesn't exist
    // Format: timestamp-randomUUID
    const timestamp = Date.now();
    let randomPart;
    
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      randomPart = crypto.randomUUID();
    } else {
      // Fallback for browsers without crypto.randomUUID
      randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    
    deviceId = `${timestamp}-${randomPart}`;
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, deviceId);
    
    return deviceId;
  } catch (e) {
    // Fallback if localStorage is not available
    const timestamp = Date.now();
    const randomPart = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    return `${timestamp}-${randomPart}`;
  }
};

// ========== COMPONENT ==========

const GeniusLivePlayer = ({
  // Props from Spike
  event,
  fixtureId,
  extras,
  
  // Display options
  showIcon = false,
  showCloseButton = true,
  
  // Callbacks
  onClose,
  onPlayerStart,
  onError,
  
  // Redux
  dispatch,
  isLogged
}) => {
  const hostRef = useRef(null);
  const handlerRef = useRef(null);
  const mountedRef = useRef(false);
  const isMountedRef = useRef(true);
  
  const [isPlayerVisible, setIsPlayerVisible] = useState(!showIcon);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hostReady, setHostReady] = useState(false);

  const resolvedFixtureId = fixtureId || event?.eventRemoteId;
  const resolvedExtras = extras || event?.extras;
  const streamAvailable = hasGeniusLiveStream(resolvedExtras);

  const mountPlayer = useCallback(async () => {
    if (!hostRef.current) return;
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    setIsError(false);
    
    if (!isLogged) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      return;
    }
    
    closePlayerIfExists();

    if (!streamAvailable) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      return;
    }
    
    const streamingData = parseBetgeniusData(resolvedExtras.BetgeniusStreamingData);
    if (!streamingData) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      return;
    }

    const hostId = `genius-player-host-${resolvedFixtureId}`;
    hostRef.current.innerHTML = '';
    const playerHost = document.createElement('div');
    playerHost.id = hostId;
    Object.assign(playerHost.style, { 
      width: '100%',
      height: '100%',
      background: '#000', 
      position: 'relative' 
    });
    hostRef.current.appendChild(playerHost);

    const device = 'MOBILE';

    const onPlayerReady = async (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.type !== 'player_ready') {
          return;
        }
        if (!isMountedRef.current) {
          return;
        }

        const permission = pickPermission(streamingData, device);
        const delivery = pickDelivery(streamingData);

        if (!permission || !delivery) {
          if (isMountedRef.current) {
            setIsError(true);
            setIsLoading(false);
          }
          onError?.('No permission or delivery');
          return;
        }

        const deviceId = getOrCreateDeviceId();

        const data = await dispatch(getBetgeniusStreamingUrl({
          fixtureId: resolvedFixtureId,
          liveStreamId: streamingData.id,
          deliveryType: delivery.deliveryType,
          deliveryTypeId: delivery.deliveryTypeId,
          device: permission.device,
          countryCode: permission.region,
          deviceId: deviceId
        }));

        if (!isMountedRef.current) {
          return;
        }

        if (window.GeniusLivePlayer?.player?.start) {
          window.GeniusLivePlayer.player.start(data);
          onPlayerStart?.();
        }
        
        setIsLoading(false);
      } catch (e) {
        if (isMountedRef.current) {
          setIsError(true);
          setIsLoading(false);
        }
        onError?.(e.message);
      }
    };

    handlerRef.current = onPlayerReady;
    window.addEventListener('geniussportsmessagebus', onPlayerReady);

    const clientId = app.config?.betgeniusClientId;
    const baseUrl = app.config?.betgeniusUrl?.replace(/\/$/, '');
    
    if (!clientId || !baseUrl) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      onError?.('Missing betgenius configuration');
      return;
    }
    
    const deviceId = getOrCreateDeviceId();
    const loaderSrc = `${baseUrl}/widgetLoader?customerId=${encodeURIComponent(clientId)}&fixtureId=${encodeURIComponent(resolvedFixtureId)}&deviceId=${encodeURIComponent(deviceId)}&autoplay=false&muted=true&_t=${Date.now()}`;

    try {
      await reloadWidgetScript(loaderSrc);
    } catch (e) {
      if (isMountedRef.current) {
        setIsError(true);
        setIsLoading(false);
      }
      onError?.(e.message);
    }
  }, [resolvedFixtureId, resolvedExtras, streamAvailable, dispatch, isLogged, onError, onPlayerStart]);

  const setHostRef = useCallback((node) => {
    hostRef.current = node;
    if (node) {
      setHostReady(true);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isPlayerVisible && hostReady && hostRef.current && !mountedRef.current) {
      mountedRef.current = true;
      mountPlayer();
    }

    return () => {
      if (handlerRef.current) {
        window.removeEventListener('geniussportsmessagebus', handlerRef.current);
        handlerRef.current = null;
      }
      closePlayerIfExists();
      mountedRef.current = false;
    };
  }, [isPlayerVisible, hostReady, mountPlayer, resolvedFixtureId]);

  const handleIconClick = useCallback(() => {
    if (!isLogged) return;
    if (!streamAvailable) return;
    setIsPlayerVisible(true);
  }, [isLogged, streamAvailable]);

  const handleClose = useCallback(() => {
    if (handlerRef.current) {
      window.removeEventListener('geniussportsmessagebus', handlerRef.current);
    }
    closePlayerIfExists();
    setIsPlayerVisible(false);
    setIsError(false);
    onClose?.();
  }, [onClose]);

  if (!isLogged || !streamAvailable) {
    return null;
  }

  if (showIcon && !isPlayerVisible) {
    return (
      <S.Wrapper>
        <S.StreamIcon 
          onClick={handleIconClick} 
          title={translation('geniusLivePlayer_watchStream') || 'Oglądaj stream na żywo'}
          dangerouslySetInnerHTML={{ __html: VideoIcon }}
        />
      </S.Wrapper>
    );
  }

  if (isError) {
    const errorMessage = translation('geniusLivePlayer_streamUnavailable') || 'Stream niedostępny';
    const closeTitle = translation('close') || 'Zamknij';
    return (
      <S.Wrapper>
        <S.ErrorBar>
          <S.ErrorMessage>{errorMessage}</S.ErrorMessage>
          {showCloseButton && (
            <S.ControlButton onClick={handleClose} title={closeTitle}>
              ✕
            </S.ControlButton>
          )}
        </S.ErrorBar>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.PlayerWrapper>
        <S.PlayerControls>
          {showCloseButton && (
            <S.ControlButton onClick={handleClose} title={translation('close') || 'Zamknij'}>
              ✕
            </S.ControlButton>
          )}
        </S.PlayerControls>
        {isLoading && (
          <S.LoadingOverlay>
            {translation('geniusLivePlayer_loading') || 'Ładowanie streamu...'}
          </S.LoadingOverlay>
        )}
        <S.PlayerHost ref={setHostRef} />
      </S.PlayerWrapper>
    </S.Wrapper>
  );
};

const mapStateToProps = (state) => {
  const { Auth: { isLogged } } = state;
  return {
    isLogged
  };
};

export default connect(mapStateToProps)(GeniusLivePlayer);
